
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppointmentBookingState, Doctor, PatientDetails, TimeSlot } from '../types';
import { format } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import { appointmentService } from '../services/appointmentService';

// Define initial state
const initialState: AppointmentBookingState = {
  step: 1,
  doctor: null,
  date: null,
  timeSlot: null,
  patientDetails: {
    fullName: '',
    email: '',
    phone: '',
    address: '',
    gender: '',
    age: '',
    country: '',
    state: '',
    city: '',
    zipCode: '',
    notes: '',
  },
};

// Define actions
type BookingAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SELECT_DOCTOR'; payload: Doctor }
  | { type: 'SELECT_DATE'; payload: Date }
  | { type: 'SELECT_TIME_SLOT'; payload: TimeSlot }
  | { type: 'UPDATE_PATIENT_DETAILS'; payload: Partial<PatientDetails> }
  | { type: 'RESET_BOOKING' };

// Create reducer
const bookingReducer = (state: AppointmentBookingState, action: BookingAction): AppointmentBookingState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload };
    case 'SELECT_DOCTOR':
      return { ...state, doctor: action.payload, step: 2 };
    case 'SELECT_DATE':
      return { ...state, date: action.payload };
    case 'SELECT_TIME_SLOT':
      return { ...state, timeSlot: action.payload, step: 3 };
    case 'UPDATE_PATIENT_DETAILS':
      return {
        ...state,
        patientDetails: { ...state.patientDetails, ...action.payload },
      };
    case 'RESET_BOOKING':
      return initialState;
    default:
      return state;
  }
};

// Create context
interface BookingContextProps {
  state: AppointmentBookingState;
  selectDoctor: (doctor: Doctor) => void;
  selectDate: (date: Date) => void;
  selectTimeSlot: (timeSlot: TimeSlot) => void;
  updatePatientDetails: (details: Partial<PatientDetails>) => void;
  goToStep: (step: number) => void;
  confirmAppointment: (patientId?: string) => Promise<void>;
  resetBooking: () => void;
}

const BookingContext = createContext<BookingContextProps | undefined>(undefined);

// Create provider
export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);
  const { toast } = useToast();

  const selectDoctor = (doctor: Doctor) => {
    dispatch({ type: 'SELECT_DOCTOR', payload: doctor });
  };

  const selectDate = (date: Date) => {
    dispatch({ type: 'SELECT_DATE', payload: date });
  };

  const selectTimeSlot = (timeSlot: TimeSlot) => {
    dispatch({ type: 'SELECT_TIME_SLOT', payload: timeSlot });
  };

  const updatePatientDetails = (details: Partial<PatientDetails>) => {
    dispatch({ type: 'UPDATE_PATIENT_DETAILS', payload: details });
  };

  const goToStep = (step: number) => {
    if (step === 4 && !isPatientFormValid()) {
      toast({
        title: "Please complete all required fields",
        description: "Full name, email, and phone are required",
        variant: "destructive",
      });
      return;
    }
    dispatch({ type: 'SET_STEP', payload: step });
  };

  const isPatientFormValid = () => {
    const { fullName, email, phone } = state.patientDetails;
    return fullName.trim() !== '' && 
           email.trim() !== '' && 
           phone.trim() !== '';
  };

  const confirmAppointment = async (patientId?: string) => {
    try {
      if (!state.doctor || !state.date || !state.timeSlot) {
        toast({
          title: "Missing information",
          description: "Please complete all steps of the booking process",
          variant: "destructive",
        });
        return;
      }

      // Check if the time slot is still available
      const formattedDate = format(state.date, 'yyyy-MM-dd');
      const isAvailable = await appointmentService.checkAvailability(
        state.doctor.id, 
        formattedDate, 
        state.timeSlot.time
      );

      if (!isAvailable) {
        toast({
          title: "Time slot no longer available",
          description: "This time slot has been booked. Please select another time.",
          variant: "destructive",
        });
        return;
      }

      // Prepare appointment data with all required fields
      const appointmentData = {
        doctorId: state.doctor.id,
        date: formattedDate,
        timeSlot: state.timeSlot.time,
        patientName: state.patientDetails.fullName,
        patientEmail: state.patientDetails.email || '',
        patientPhone: state.patientDetails.phone,
        patientAddress: state.patientDetails.address || '',
        patientGender: state.patientDetails.gender || '',
        patientAge: state.patientDetails.age ? parseInt(state.patientDetails.age, 10) : undefined,
        patientId: patientId, // Include the patient ID if available
        status: 'confirmed' as const,
      };
      
      console.log('Creating appointment with data:', appointmentData);
      
      // Create appointment via API
      const createdAppointment = await appointmentService.create(appointmentData);
      console.log('Appointment created successfully:', createdAppointment);
      
      toast({
        title: "Appointment confirmed!",
        description: `Your appointment with ${state.doctor.name} on ${format(state.date, 'MMMM do, yyyy')} at ${state.timeSlot.time} has been scheduled.`,
      });
      
      // Move to confirmation step
      dispatch({ type: 'SET_STEP', payload: 5 });
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast({
        title: "Appointment booking failed",
        description: error.message || "There was an error creating your appointment. Please try again.",
        variant: "destructive",
      });
      throw error; // Rethrow so the AppointmentReview component can handle it
    }
  };

  const resetBooking = () => {
    dispatch({ type: 'RESET_BOOKING' });
  };

  return (
    <BookingContext.Provider
      value={{
        state,
        selectDoctor,
        selectDate,
        selectTimeSlot,
        updatePatientDetails,
        goToStep,
        confirmAppointment,
        resetBooking,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

// Create hook
export const useBooking = (): BookingContextProps => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};
